/**
 * Fence metadata — ```` ```ts title="src/main.ts" ```` — is dropped by
 * `remark-rehype` on its way to the DOM, so it is lifted onto the mdast as a
 * data attribute first and read back off the hast node in the renderer.
 *
 * Ported from t3code's `remarkPreserveCodeMeta` and the fence-meta readers that
 * go with it, along with `remarkTagInlineCode`, which marks inline spans so a
 * file-link chip can claim them.
 */
type MarkdownAstNode = {
  type?: string;
  meta?: unknown;
  data?: {
    hProperties?: Record<string, unknown>;
  };
  children?: MarkdownAstNode[];
};

function preserveCodeMeta(node: MarkdownAstNode): void {
  if (node.type === "code" && typeof node.meta === "string" && node.meta.trim().length > 0) {
    node.data = {
      ...node.data,
      hProperties: { ...node.data?.hProperties, dataCodeMeta: node.meta.trim() },
    };
  }
  node.children?.forEach(preserveCodeMeta);
}

export function remarkPreserveCodeMeta() {
  return (tree: MarkdownAstNode) => preserveCodeMeta(tree);
}

const FENCE_TITLE_ATTR_REGEX = /(?:^|\s)(?:title|file(?:name)?)=(?:"([^"]+)"|'([^']+)'|(\S+))/i;
const FENCE_FILENAME_TOKEN_REGEX = /^[\w@][\w@./-]*\.[A-Za-z0-9]+$/;

/** Pulls a filename out of fence meta: ```ts title="x.ts" / ```ts src/main.ts */
export function extractFenceTitle(meta: string | undefined): string | null {
  if (!meta) return null;
  const attrMatch = FENCE_TITLE_ATTR_REGEX.exec(meta);
  const attrTitle = attrMatch?.[1] ?? attrMatch?.[2] ?? attrMatch?.[3];
  if (attrTitle) return attrTitle;
  return meta.split(/\s+/).find((candidate) => FENCE_FILENAME_TOKEN_REGEX.test(candidate)) ?? null;
}

/** Reads the meta back off the hast `<pre>` node the renderer hands us. */
export function extractPreCodeMeta(node: unknown): string | undefined {
  const children = (
    node as
      | {
          children?: Array<{
            type?: string;
            tagName?: string;
            data?: { meta?: unknown };
            properties?: { dataCodeMeta?: unknown };
          }>;
        }
      | undefined
  )?.children;
  const codeNode = children?.find((child) => child?.type === "element" && child.tagName === "code");
  const meta = codeNode?.properties?.dataCodeMeta ?? codeNode?.data?.meta;
  return typeof meta === "string" && meta.trim().length > 0 ? meta.trim() : undefined;
}

/**
 * Mark inline code spans on the mdast.
 *
 * Fenced blocks and inline spans both arrive at the `code` renderer as
 * `<code>`, and by then the distinction is gone — so it is recorded here, where
 * the node types still differ. Only inline spans are eligible to become file
 * links; linkifying a whole fenced block would be nonsense.
 *
 * Code inside a link label stays untagged: turning it into a chip would nest an
 * anchor inside the link's own anchor, which is invalid HTML and steals the
 * outer link's clicks.
 */
export function remarkTagInlineCode() {
  return (tree: MarkdownAstNode) => tagInlineCode(tree, false);
}

function tagInlineCode(node: MarkdownAstNode, insideLink: boolean): void {
  if (node.type === "inlineCode" && !insideLink) {
    node.data = { ...node.data, hProperties: { ...node.data?.hProperties, dataInlineCode: "" } };
  }
  const childInsideLink = insideLink || node.type === "link" || node.type === "linkReference";
  node.children?.forEach((child) => tagInlineCode(child, childInsideLink));
}
